import { useEffect } from "react";
import { DropdownOption } from "@coherehealth/common";
import { ReferenceDataItem } from "@coherehealth/qm-api";
import { FormValues, FieldId } from "./fieldRegistry";

/**
 * Side-effect hook: removes any already-selected outcome options that are no longer
 * in `filteredOutcomes` when the case type selection changes.
 *
 * Intentionally excludes `formValues.outcome` from the dependency array so the effect
 * fires only when the filtered set changes (case type change), not on every outcome
 * selection — including it would cause an infinite loop.
 *
 * The `filteredOutcomes.length` guard prevents wiping selected outcomes while the
 * reference-data endpoint is loading or during a brief null/refetch window.
 */
export function useOutcomeAutoRemoval(
  selectedCaseTypeIds: string[],
  filteredOutcomes: ReferenceDataItem[],
  formValues: FormValues,
  setField: (field: FieldId, value: unknown) => void
): void {
  useEffect(() => {
    if (!selectedCaseTypeIds.length) {
      return;
    }
    if (!filteredOutcomes.length) {
      return;
    }
    const current = formValues.outcome as DropdownOption[] | undefined;
    if (!current?.length) {
      return;
    }
    const validIds = new Set(filteredOutcomes.map((o) => o.value ?? ""));
    const filtered = current.filter((o) => validIds.has(o.id));
    if (filtered.length !== current.length) {
      setField("outcome", filtered);
    }
    // formValues.outcome intentionally excluded: fires only on filter change, not on every outcome selection.
    // setField is stable (useCallback with [] deps in useSearchFormState).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOutcomes, setField]);
}
